import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback,
  useMemo,
  ChangeEvent,
} from "react";
import { Combobox, ComboboxInput, Transition } from "@headlessui/react";
import { useLocation } from "wouter";
import { useClickOutside } from "../hooks/useClickOutside"; // Ensure this hook is correctly implemented

// Icon classes mapping
const iconClass = {
  author: "d-user-profile-circle",
  artwork: "d-photo",
  collection: "d-network-policy",
};

// Interfaces
interface SearchItem {
  name: string;
  type: "author" | "artwork" | "collection";
  uuid: string;
}

interface SearchFieldProps {
  onClose: () => void;
  onSelected: (selection: SearchItem) => void;
}

// Custom Hook for Search Logic
const useSearch = (query: string, maxResults: number = 12) => {
  const [results, setResults] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://admin.collectieconnectie.nl/api/v1/search?query=${encodeURIComponent(
            query,
          )}`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const formattedData = formatResults(data, maxResults);
        setResults(formattedData);
      } catch (error) {
        console.error("Error fetching search data:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceFetch = setTimeout(fetchData, 300); // Debounce by 300ms

    return () => clearTimeout(debounceFetch);
  }, [query, maxResults]);

  const formatResults = (data: any, maxResults: number): SearchItem[] => {
    const { authors = [], artworks = [], collections = [] } = data;

    const formattedAuthors = authors.map((author: any) => ({
      name: author.name,
      type: "author" as const,
      uuid: author.id,
    }));
    const formattedArtworks = artworks.map((artwork: any) => ({
      name: artwork.title,
      type: "artwork" as const,
      uuid: artwork.uuid,
    }));
    const formattedCollections = collections.map((collection: any) => ({
      name: collection.name,
      type: "collection" as const,
      uuid: collection.id,
    }));

    const totalResults =
      formattedAuthors.length +
      formattedArtworks.length +
      formattedCollections.length;

    if (totalResults === 0) return [];

    const sliceCount = (count: number) =>
      Math.round((maxResults * count) / totalResults);

    const slicedAuthors = formattedAuthors.slice(
      0,
      sliceCount(formattedAuthors.length),
    );
    const slicedArtworks = formattedArtworks.slice(
      0,
      sliceCount(formattedArtworks.length),
    );
    const slicedCollections = formattedCollections.slice(
      0,
      sliceCount(formattedCollections.length),
    );

    return [...slicedAuthors, ...slicedArtworks, ...slicedCollections];
  };

  return { results, loading };
};

// Sub-Components

// 1. Icons Component
const Icons: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <>
    {/* Search Icon */}
    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.817-4.817A6 6 0 012 8z"
          clipRule="evenodd"
        />
      </svg>
    </span>
    {/* Close Icon */}
    <span
      onClick={onClose}
      className="cursor-pointer d-icon d-close text-white absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center h-10"
    ></span>
  </>
);

// 2. SearchInput Component
const SearchInput: React.FC<{
  query: string;
  setQuery: (value: string) => void;
  onClose: () => void;
}> = ({ query, setQuery, onClose }) => (
  <div className="relative w-full text-left">
    <ComboboxInput
      className="w-full h-10 border-2 border-white/50 rounded-full  bg-transparent backdrop--blur text-white pl-10 pr-10 focus:outline-none"
      placeholder="Search..."
      value={query}
      onChange={(event: any) => setQuery(event.target.value)}
      displayValue={(item: SearchItem | null) => item?.name || ""}
    />
    <Icons onClose={onClose} />
  </div>
);

// Helper function to escape regex special characters
const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

// 3. SearchResults Component with Highlighting
const SearchResults: React.FC<{
  results: SearchItem[];
  loading: boolean;
  query: string;
}> = ({ results, loading, query }) => {
  // Helper function to highlight matched text
  const highlightText = useCallback((text: string, highlight: string) => {
    if (!highlight) return text;

    const escapedHighlight = escapeRegExp(highlight);
    const regex = new RegExp(`(${escapedHighlight})`, "gi");
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className="font-bold ">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    );
  }, []);

  const renderedResults = useMemo(() => {
    return results.map((item) => (
      <Combobox.Option
        key={item.uuid}
        value={item}
        className={({ active }) =>
          `search-result flex items-center relative cursor-pointer select-none py-[0.4rem] px-0 !m-0 ${
            active ? "" : ""
          }`
        }
      >
        <div className="flex-shrink-0 mr-3">
          <span
            className={`d-icon ${iconClass[item.type]} flex items-center justify-center`}
          ></span>
        </div>
        <span className={`flex-grow truncate font-normal`}>
          {highlightText(item.name, query)}
        </span>
      </Combobox.Option>
    ));
  }, [results, query, highlightText]);

  return (
    <Transition
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      afterLeave={() => {}}
    >
      <Combobox.Options
        id="search-results"
        className="absolute z-10 w-full mt-1 bg-transparent border-2 border-white/50 rounded-md backdrop--blur max-h-80 overflow-y-auto space-y-1"
      >
        {loading && (
          <div className="cursor-default select-none relative text-white py-2 px-3">
            Loading...
          </div>
        )}
        {!loading && results.length === 0 && (
          <div className="cursor-default select-none relative text-white py-2 px-3">
            No results found.
          </div>
        )}
        {!loading && renderedResults}
      </Combobox.Options>
    </Transition>
  );
};

// Main SearchField Component
const SearchField: React.FC<SearchFieldProps> = ({ onClose, onSelected }) => {
  const [query, setQuery] = useState("");
  const { results, loading } = useSearch(query);
  const [, setLocation] = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the custom hook to detect clicks outside the container
  useClickOutside(containerRef, onClose);

  const handleSelection = useCallback(
    (item: SearchItem | null) => {
      if (item === null) {
        return;
      }

      if (item.type === "artwork") {
        setLocation(`/collection/${item.uuid}`);
      } else {
        onSelected(item);
      }
      onClose();
    },
    [onClose, onSelected, setLocation],
  );

  return (
    <div
      id="search-field"
      className="absolute top-0 right-0 w-full max-w-md md:max-w-[30rem]"
      ref={containerRef}
    >
      <Combobox onChange={handleSelection}>
        <div className="relative">
          <SearchInput query={query} setQuery={setQuery} onClose={onClose} />
          {/* Pass the query to SearchResults */}
          <SearchResults results={results} loading={loading} query={query} />
        </div>
      </Combobox>
    </div>
  );
};

export default SearchField;
